import React, { useContext, useEffect, useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Box, IconButton } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import StyledBgContainer from '../../../components/StyledBgContainer/StyledBgContainer';
import StyledButton from '../../../components/Button/Button';
import Description from '../../../components/Description/Description';
import TopNav from '../../../components/TopNav/TopNav';
import { ProfileContext } from '../../../context/ProfileContext';
import { editProfile, getStage } from '../../../services/NetworkServices';
import { storeStageNumber } from '../../../utils/auth-token-utils/cookie';
import ApiStatus from '../../../config/apiStatusConstants';
import UploadButtonIcon from '../../../assets/UploadButtonIcon.svg';
import './UpdateBusinessCard.css';

const UpdateBusinessCard: React.FC = () => {
  const navigate = useNavigate();

  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('ProfileContext must be used within a ProfileProvider');
  }

  const {
    userProfile,
    handleFrontFile,
    handleBackFile,
    updatedFrontFile,
    updatedBackFile,
    updatedFrontPreview,
    updatedBackPreview,
    isGovtIdFrontChanged,
    isGovtIdBackChanged,
  } = context;

  const [apiStatus, setApiStatus] = useState<ApiStatus>(ApiStatus.INITIAL);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const handleSaveChanges = async () => {
    if (!isGovtIdFrontChanged && !isGovtIdBackChanged) {
      navigate(-1);
      return;
    }
    try {
      setApiStatus(ApiStatus.LOADING);
      const updatedData = new FormData();
      if (updatedFrontFile) {
        updatedData.append('idFront', updatedFrontFile);
      }
      if (updatedBackFile) {
        updatedData.append('idBack', updatedBackFile);
      }
      const response = await editProfile(updatedData);
      if (response.data) {
        setApiStatus(ApiStatus.SUCCESS);
        navigate(-1);
      }
    } catch (error: any) {
      setApiStatus(ApiStatus.FAILURE);
      setErrorMsg(
        error?.response?.data?.error?.message || 'Failed to update business card',
      );
    }
  };

  console.log('updated front preview is', updatedFrontPreview);
  console.log('user profile is', userProfile);

  const onChangeGovtId = (event: ChangeEvent<HTMLInputElement>) => {
    console.log('on change event triggered');
    const { name, files } = event.target;
    if (files && files.length > 0) {
      const file = files[0];
      console.log('file is', file);
      if (name === 'frontId') {
        handleFrontFile(file);
      } else if (name === 'backId') {
        handleBackFile(file);
      }
    }
  };

  useEffect(() => {
    const fetchStageDetails = async () => {
      try {
        console.log('inside use effect');
        const response = await getStage();
        const data = await response.data;
        if (data) {
          console.log('stage is', data.data.stage);
          const stage = data.data.stage;
          storeStageNumber(stage);
          if (stage === '1') {
            navigate('/supervisor/createProfile');
          }
        }
      } catch (error) {
        console.error('Error fetching profile details:', error);
      }
    };
    fetchStageDetails();
  }, [navigate]);

  return (
    <StyledBgContainer>
      <Grid
        container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
        }}
      >
        <TopNav showArrow={true} />
        <Grid item xs={12}>
          <h1>Upload Business Card</h1>
        </Grid>
        <Grid item xs={12}>
          <Description>
            Please upload a valid and legible image of your new business card.
          </Description>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={4}>
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center', // Center align items within the column
              }}
            >
              <Box
                sx={{
                  minWidth: '200px',
                  width: '100%', // Ensure the box takes full width of the Grid item
                  height: '200px', // Set a fixed height
                  borderRadius: '16px', // Optional: round corners
                  overflow: 'hidden', // Hide overflow
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderColor: '#365F91',
                  border: ' 1.2px solid transparent',
                  background: !(isGovtIdFrontChanged
                    ? updatedFrontPreview
                    : userProfile.idFront)
                    ? `
                  linear-gradient(black, black) padding-box,linear-gradient(180deg, #365f91 -4.29%, #365f9133 100%) border-box `
                    : 'null',
                }}
              >
                {(
                  isGovtIdFrontChanged
                    ? updatedFrontPreview
                    : userProfile.idFront
                ) ? (
                  <label
                    htmlFor="change-govt-front-id"
                    style={{
                      height: '200px',
                      width: '200px',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={
                        isGovtIdFrontChanged
                          ? updatedFrontPreview
                          : userProfile.idFront || ''
                      }
                      alt="Front ID"
                      style={{
                        width: '100%', // Make the image responsive
                        height: '100%', // Make the image responsive
                        objectFit: 'cover', // Ensure it covers the box without stretching
                      }}
                    />
                  </label>
                ) : (
                  <div>
                    <label htmlFor="change-govt-front-id">
                      <IconButton
                        color="primary"
                        aria-label="update business card"
                        component="span"
                      >
                        <img src={UploadButtonIcon} alt="Upload Icon" />
                      </IconButton>
                    </label>
                    <p style={{ color: 'white' }}>Upload</p>
                  </div>
                )}
              </Box>
              <input
                name="frontId"
                accept="image/*"
                style={{ display: 'none' }}
                id="change-govt-front-id"
                type="file"
                onChange={onChangeGovtId}
              />
              <div className="update-govt-id">
                <label htmlFor="change-govt-front-id">Update Front</label>
              </div>
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center', // Center align items within the column
              }}
            >
              <Box
                sx={{
                  minWidth: '200px',
                  width: '100%', // Ensure the box takes full width of the Grid item
                  height: '200px', // Set a fixed height
                  borderRadius: '16px', // Optional: round corners
                  overflow: 'hidden', // Hide overflow
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderColor: '#365F91',
                  border: ' 1.2px solid transparent',
                  background: !(isGovtIdBackChanged
                    ? updatedBackPreview
                    : userProfile.idBack)
                    ? `
                  linear-gradient(black, black) padding-box,linear-gradient(180deg, #365f91 -4.29%, #365f9133 100%) border-box `
                    : 'null',
                }}
              >
                {(
                  isGovtIdBackChanged ? updatedBackPreview : userProfile.idBack
                ) ? (
                  <label
                    htmlFor="change-govt-back-id"
                    style={{
                      height: '200px',
                      width: '200px',
                      cursor: 'pointer',
                    }}
                  >
                    <img
                      src={
                        isGovtIdBackChanged
                          ? updatedBackPreview
                          : userProfile.idBack || ''
                      }
                      alt="Back ID"
                      style={{
                        width: '100%', // Make the image responsive
                        height: '100%', // Make the image responsive
                        objectFit: 'cover', // Ensure it covers the box without stretching
                      }}
                    />
                  </label>
                ) : (
                  <div>
                    <label htmlFor="change-govt-back-id">
                      <IconButton
                        color="primary"
                        aria-label="update business card"
                        component="span"
                      >
                        <img src={UploadButtonIcon} alt="Upload Icon" />
                      </IconButton>
                    </label>
                    <p style={{ color: 'white' }}>Upload</p>
                  </div>
                )}
              </Box>
              <input
                name="backId"
                accept="image/*"
                style={{ display: 'none' }}
                id="change-govt-back-id"
                type="file"
                onChange={onChangeGovtId}
              />
              <div className="update-govt-id">
                <label htmlFor="change-govt-back-id">Update Back</label>
              </div>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {apiStatus === ApiStatus.LOADING ? (
            <StyledButton sx={{ marginTop: '6vh' }}>
              <CircularProgress size={24} />
            </StyledButton>
          ) : (
            <StyledButton
              sx={{ marginTop: '6vh' }}
              onClick={handleSaveChanges}
            >
              Update
            </StyledButton>
          )}
          {apiStatus === ApiStatus.FAILURE && (
            <p style={{ color: 'white' }}>{errorMsg}</p>
          )}
        </Grid>
      </Grid>
    </StyledBgContainer>
  );
};

export default UpdateBusinessCard;
